/**
 * Brand memory (Figma 09 · Brand memory 17–19), Owner only. The agents still
 * save memories from Contributor chats and the distiller still learns from
 * client reviews; only the owner sees and curates the list.
 */
import { useMemo } from "react";
import {
  useBrandMemories,
  useCreateBrandMemory,
  useUpdateBrandMemory,
  useDeleteBrandMemory,
  usePreviewBrandMemoryImport,
  useSaveBrandMemoryImport,
  type BrandMemorySource,
} from "@/app/routes/dashboard/settings/api/useBrandMemories";
import { brandMemoryExport } from "@/app/routes/dashboard/settings/memoryExport";
import MemoryManager, { type MemoryManagerCopy } from "../memory/MemoryManager";
import { PageHead, SettingsPage } from "../parts/SettingsParts";
import { NeedBrand, useBrandHead } from "../parts/scope";

/** Mirrors BRAND_MEMORY_BODY_MAX_LENGTH in the backend's utils/enums/BrandMemory.ts. */
const BODY_MAX_LENGTH = 300;

const SOURCE_LABELS: Record<BrandMemorySource, string> = {
  manual: "Manual",
  chat: "From chat",
  client_feedback: "From client feedback",
  import: "Imported",
};

export default function BrandMemoryPage() {
  const head = useBrandHead();
  const brandId = head.brand?.id ?? null;
  const { data, isLoading, isError, refetch } = useBrandMemories(brandId);
  const createMemory = useCreateBrandMemory(brandId);
  const updateMemory = useUpdateBrandMemory(brandId);
  const deleteMemory = useDeleteBrandMemory(brandId);
  const previewImport = usePreviewBrandMemoryImport(brandId);
  const saveImport = useSaveBrandMemoryImport(brandId);
  const exportFormat = useMemo(() => brandMemoryExport(head.brandName), [head.brandName]);

  const copy: MemoryManagerCopy = {
    addTitle: `Add to ${head.brandName}’s memory`,
    emptyTitle: `Nothing remembered about ${head.brandName} yet`,
    empty:
      "The agents add to it from chats and client feedback, and you can add your own. Each memory says where it came from.",
    placeholder: `One fact or preference about ${head.brandName}, in one sentence.`,
    full: "This brand has 30 of 30. Delete one to add or import another.",
    importDescription: `Standing preferences about ${head.brandName}, from your files or pasted text. You pick which to keep.`,
    loadErrorTitle: `${head.brandName}’s memory did not load`,
    loadError: "Creo could not read it. Nothing has changed, and the agents still use it.",
    loadingCaption: `Loading ${head.brandName}’s memory`,
  };

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Brand memory"
        status={`What the agents remember about ${head.brandName}. They read it before planning, ideas and briefs. Only owners see this page.`}
      />
      {!brandId ? (
        <NeedBrand />
      ) : (
        <MemoryManager
          memories={data?.memories ?? []}
          cap={data?.cap ?? 0}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          canEdit
          bodyMaxLength={BODY_MAX_LENGTH}
          sourceLabels={SOURCE_LABELS}
          copy={copy}
          onCreate={(body) => createMemory.mutate(body)}
          onUpdate={(id, body) => updateMemory.mutate({ id, body })}
          onDelete={(id) => deleteMemory.mutate(id)}
          importPreview={previewImport}
          onImport={(bodies) => saveImport.mutate(bodies)}
          exportFormat={exportFormat}
        />
      )}
    </SettingsPage>
  );
}
